import { defineStore } from 'pinia';
import { reactive, computed } from 'vue';
import _ from 'lodash';
import standardize from '@/helper/standardization';
import { MediaType, TextFields, MediaFields, BrandLayerTypes } from '@/helper/consts';
import sceneCacheStorage from '@/helper/cache';
import brandsHelper from '@/helper/brands';
import getMediaType from '@/helper/getMediaType';

export const useBuilderStore = defineStore('builder', () => {
  const state = reactive({
    idmScene: null,
    originalScene: null,
    actualScene: null,
    libraries: [],
    brands: [],
    updatedNodes: [],
    currentBrandId: null,
    nodesByKey: null,
    hoveredNode: null,
    hoveredOnCanvas: false,
    selectedNode: null,
    debugMode: false,
    useCache: true,
    previewLoading: false,
  });

  const idmScene = computed(() => state.idmScene);
  const scene = computed(() => state.actualScene);
  const libraries = computed(() => state.libraries);
  const brands = computed(() => state.brands);
  const updatedNodes = computed(() => state.updatedNodes);
  const currentBrandId = computed(() => state.currentBrandId);
  const hoveredNode = computed(() => state.hoveredNode);
  const hoveredOnCanvas = computed(() => state.hoveredOnCanvas);
  const selectedNode = computed(() => state.selectedNode);
  const debugMode = computed(() => state.debugMode);
  const previewLoading = computed(() => state.previewLoading);

  function setIdmScene({ idmScene }) {
    state.idmScene = idmScene;
  }
  function updateScene({ newScene, reSave = false, useCache = true }) {
    if (reSave || !state.originalScene || state.originalScene.id !== newScene.id) {
      state.originalScene = JSON.parse(JSON.stringify(newScene));
    }
    state.actualScene = newScene;

    if (state.useCache && useCache && !reSave) {
      let sceneCache = sceneCacheStorage.getSceneCache(state.actualScene.id);
      if (sceneCache) {
        state.currentBrandId = sceneCache.brandId;
        state.updatedNodes = _.get(sceneCache, ['updatedNodes'], []);
      }
      if (sceneCache && sceneCache.last_modified === state.actualScene.last_modified) {
        state.actualScene.tags = sceneCache.tags;
        state.actualScene.description = sceneCache.description;
        const availableFields = [...TextFields, ...['name', 'val', 'is_hidden', 'media_type', 'tags', 'branding', 'fileName', 'mediaLink'], ...MediaFields];
        _.forEach(sceneCache.nodes, (nodeCache) => {
          let node = _.find(state.actualScene.nodes, { key: nodeCache.key });
          if (node) {
            _.forEach(nodeCache, (value, key) => {
              if (_.indexOf(availableFields, key) >= 0) {
                let val = JSON.parse(JSON.stringify(value));
                _.set(node, key, val);
              }
            });
          }
        });
      }
    }

    state.nodesByKey = {
      original: {},
      actual: {},
    };

    state.originalScene.nodes.forEach((node) => {
      state.nodesByKey.original[node.key] = node;
    });
    state.actualScene.nodes.forEach((node) => {
      state.nodesByKey.actual[node.key] = node;
    });

    state.actualScene.nodes.forEach((node) => {
      const originalNode = state.nodesByKey.original[node.key];
      if (node.media_type === MediaType.TEXT) {
        node.edited = computed(() =>
          !_.isEqual(_.pick(node, TextFields), _.pick(originalNode, TextFields)) ||
          !_.isEqual(node.tags.sort(), originalNode.tags.sort()) ||
          !_.isEqual(node.description, originalNode.description)
        );
      } else {
        node.edited = computed(() =>
          JSON.stringify(node.val) !== JSON.stringify(originalNode.val) ||
          JSON.stringify(node.alignment_scale_type) !== JSON.stringify(originalNode.alignment_scale_type) ||
          JSON.stringify(node.alignment_x_align) !== JSON.stringify(originalNode.alignment_x_align) ||
          JSON.stringify(node.alignment_y_align) !== JSON.stringify(originalNode.alignment_y_align) ||
          JSON.stringify(node.if_longer) !== JSON.stringify(originalNode.if_longer) ||
          node.is_hidden !== originalNode.is_hidden ||
          !_.isEqual(node.tags.sort(), originalNode.tags.sort()) ||
          !_.isEqual(node.description, originalNode.description)
        );
      }
      node.renamed = computed(() => originalNode.name !== node.name);
    });

    state.actualScene.edited = computed(() =>
      _.isObject(_.find(state.actualScene.nodes, { edited: true })) ||
      state.actualScene.description !== state.originalScene.description ||
      !_.isEqual(state.actualScene.tags.sort(), state.originalScene.tags.sort())
    );
    state.actualScene.renamed = computed(() => _.isObject(_.find(state.actualScene.nodes, { renamed: true })));
  }
  function updateSceneDescription(data) {
    state.actualScene.description = data;
  }
  function updateSceneTags(data) {
    state.actualScene.tags = data;
  }
  function updateSceneProtectedRegions(data) {
    state.actualScene.protected_regions = data;
  }
  function setLibraries(libs) {
    state.libraries = libs;
  }
  function setBrands(brs) {
    state.brands = brs;
  }
  function setCurrentBrandId(brandId) {
    state.currentBrandId = brandId;
    sceneCacheStorage.updateBrandId(state.actualScene, brandId);
  }
  function applyBrandStyles(brandId) {
    // RESET BRANDED NODES TO DEFAULT/CHANGED
    if (state.currentBrandId) {
      const changedNodes = _.filter(state.actualScene.nodes, { branding: true });
      changedNodes.forEach(changedNode => {
        const originalNode = state.nodesByKey.original[changedNode.key];
        changedNode.media_type = originalNode.media_type;
        if (originalNode.media_type === MediaType.TEXT) {
          const filteredVal = _.map(changedNode.val, (nodeVal) => {
            return _.omit(nodeVal, ['font_path', 'font_checksum']);
          });
          changedNode.asset_path = originalNode.asset_path;
          changedNode.val = filteredVal;
        } else {
          changedNode.val = JSON.parse(JSON.stringify(originalNode.val));
        }
        changedNode.branding = false;
      });
    }

    // APPLY BRAND STYLES
    if (brandId) {
      let brandedNodes = {};

      state.actualScene.nodes.forEach(node => {
        const tags = _.map(node.tags, (tag) => ({ name: tag, value: null }));
        _.set(brandedNodes, node.name, { placeholder: null, tags });
      });

      const brandKit = _.find(state.brands, { '_id': brandId });
      const brandLayers = _.get(brandKit, 'layres', []);

      brandLayers.forEach(brandLayer => {
        if (_.isEmpty(brandLayer)) return;
        const reg = brandsHelper.layerRegex(brandLayer);

        state.actualScene.nodes.forEach(node => {
          if (node.media_type === MediaType.TEXT && brandLayer.type !== BrandLayerTypes.TEXT) return;
          if (node.media_type !== MediaType.TEXT && brandLayer.type !== BrandLayerTypes.MEDIA) return;

          node.tags.forEach(tag => {
            if (tag === brandLayer.name.toLowerCase()) {
              const brandedNode = _.get(brandedNodes, [node.name]);
              const index = _.findIndex(brandedNode.tags, { name: brandLayer.name.toLowerCase() });
              if (index !== -1) {
                brandedNode.tags[index] = _.assign({}, brandedNode.tags[index], { value: brandLayer });
              }
            }
          });

          if (reg.test(node.name)) {
            _.set(brandedNodes, [node.name, 'placeholder'], brandLayer);
          }
        });
      });

      _.forEach(brandedNodes, (brandedNode, nodeKey) => {
        const lastTagWithBrand = _.last(_.filter(brandedNode.tags, tag => tag.value !== null));
        const brandLayer = lastTagWithBrand !== undefined ? lastTagWithBrand.value : brandedNode.placeholder;
        if (brandLayer) {
          let node = _.find(state.actualScene.nodes, { key: nodeKey });

          brandLayer.values.forEach(objValue => {
            _.forOwn(objValue, (value, key) => {
              if (brandLayer.type === BrandLayerTypes.TEXT) {
                const filteredVal = _.map(node.val, (nodeVal) => {
                  return _.omit(nodeVal, ['font_path', 'font_checksum']);
                });
                node.asset_path = value;
                node.val = filteredVal;
              } else if (brandLayer.type === BrandLayerTypes.MEDIA) {
                const mediaType = brandsHelper.isColor(value) ? 'color' : getMediaType(value);
                node.val = mediaType === 'color' ? brandsHelper.getRgbColor(value) : value;
                node.media_type = mediaType;
              }
              node.branding = true;
            });
          });
        }
      });
    }

    setCurrentBrandId(brandId);
  }
  function hoverNode(data) {
    state.hoveredNode = data?.node || null;
    state.hoveredOnCanvas = !!data?.node && !!data?.onCanvas;
  }
  function selectNode(node) {
    state.selectedNode = node || null;
  }
  function updateNodeValue(data) {
    if(!data.node || !data.val){
      new Error('incorrect data');
    }
    const actualNode = state.nodesByKey.actual[data.node.key];
    switch (actualNode.media_type) {
      case MediaType.TEXT:
        _.forEach(data.data, (value, key) => {
          if (_.indexOf(TextFields, key) >= 0) {
            let val = key === 'font_size' ? parseInt(value) : value;
            _.set(actualNode, key, val);
          }
        });
        break;
      case MediaType.AUDIO:
        actualNode.val = data.data.val;
        if (data.data.fileName) actualNode.fileName = data.data.fileName;
        if (data.data.mediaLink) actualNode.mediaLink = data.data.mediaLink;
        break;
      case MediaType.VIDEO:
      case MediaType.COLOR:
      case MediaType.IMAGE:
        actualNode.val = data.data.val;
        actualNode.media_type = data.data.media_type;
        actualNode.alignment_scale_type = data.data.alignment_scale_type;
        actualNode.alignment_x_align = data.data.alignment_x_align;
        actualNode.alignment_y_align = data.data.alignment_y_align;
        actualNode.if_longer = data.data.if_longer;
        if (data.data.fileName) actualNode.fileName = data.data.fileName;
        if (data.data.mediaLink) actualNode.mediaLink = data.data.mediaLink;
        if (data.data.color) actualNode.color = data.data.color;
        break;
      default:
        actualNode.val = data.data.val;
    }
  }
  function updateNodeTags(data) {
    if (!data.node || !data.hasOwnProperty('tags')) {
      new Error('incorrect data');
    }
    const actualNode = state.nodesByKey.actual[data.node.key];
    actualNode.tags = data.tags;
  }
  function updateNodeDescription(data) {
    if (!data.node || !data.hasOwnProperty('description')) {
      new Error('incorrect data');
    }
    const actualNode = state.nodesByKey.actual[data.node.key];
    actualNode.description = data.description;
  }
  function showNode(node) {
    if (!node) {
      new Error('incorrect data');
    }
    node.is_hidden = false;
  }
  function hideNode(node) {
    if (!node) {
      new Error('incorrect data');
    }
    node.is_hidden = true;
  }
  function undoNodeChanges(data = {}) {
    const node = data.node || state.selectedNode;
    const removeTags = data.removeTags !== undefined ? data.removeTags : false;
    const originalNode = state.nodesByKey.original[node.key];
    const actualNode = state.nodesByKey.actual[node.key];

    if (actualNode.renamed) {
      actualNode.name = originalNode.name;
    }

    if (!actualNode.edited) {
      return;
    }

    if (removeTags) {
      actualNode.tags = originalNode.tags;
    }

    actualNode.description = originalNode.description;

    switch (actualNode.media_type) {
      case MediaType.TEXT:
        actualNode.name = originalNode.name;
        _.forEach(originalNode, (value, key) => {
          if (_.indexOf(TextFields, key) >= 0) {
            if (actualNode.branding && key === 'asset_path') return;
            _.set(actualNode, key, JSON.parse(JSON.stringify(value)));
          }
        });
        const extraKeys = _.pull(_.keys(_.pickBy(actualNode, (val, key) => !_.has(originalNode, key))), 'edited', 'renamed');
        extraKeys.map(extraKey => delete actualNode[extraKey]);
        break;
      case MediaType.AUDIO:
        actualNode.val = JSON.parse(JSON.stringify(originalNode.val));
        actualNode.fileName = null;
        actualNode.mediaLink = null;
        break;
      case MediaType.VIDEO:
      case MediaType.COLOR:
      case MediaType.IMAGE:
        actualNode.val = JSON.parse(JSON.stringify(originalNode.val));
        actualNode.media_type = originalNode.media_type;
        if (originalNode.alignment_scale_type) {
          actualNode.alignment_scale_type = originalNode.alignment_scale_type;
        }
        if (typeof originalNode.alignment_x_align !== 'undefined' && typeof originalNode.alignment_y_align !== 'undefined') {
          actualNode.alignment_x_align = originalNode.alignment_x_align;
          actualNode.alignment_y_align = originalNode.alignment_y_align;
        }
        if (typeof originalNode.if_longer !== 'undefined') {
          actualNode.if_longer = originalNode.if_longer;
        }
        actualNode.fileName = null;
        actualNode.mediaLink = null;
        actualNode.color = null;

        if (state.currentBrandId && actualNode.branding) {
          const brandKit = _.find(state.brands, { '_id': state.currentBrandId });
          const brandValue = brandsHelper.getNodeBrandValue(actualNode, brandKit);
          _.forOwn(brandValue, (value, key) => {
            actualNode[key] = JSON.parse(JSON.stringify(value));
          });
        }
        break;
      default:
        if (!actualNode.branding) {
          actualNode.val = JSON.parse(JSON.stringify(originalNode.val));
        }
        actualNode.is_hidden = originalNode.is_hidden;
    }

    const index = state.actualScene.nodes.findIndex(n => n.key === actualNode.key);
    if (index !== -1) {
      state.actualScene.nodes[index] = actualNode;
    }
    if(state.selectedNode?.key === actualNode.key) {
      state.selectedNode = actualNode;
    }
  }
  function setDebugMode(mode) {
    state.debugMode = mode;
  }
  function changePreviewLoading(val) {
    state.previewLoading = val;
  }
  function standardization(data) {
    const idmScene = data.scene;
    const newScene = standardize(data.scene, data.placeholders, data.frames);
    normalizeTextFlowFromRoot(newScene);
    setIdmScene({ idmScene });
    updateScene({ newScene, reSave: false, useCache: true });
  }

  function normalizeTextFlowFromRoot(scene) {
    scene.nodes.forEach(node => {
      if (node.media_type !== 'text') return;
      if (!Array.isArray(node.val)) return;

      const rootTracking = node.tracking ?? null;
      const rootLeading = node.leading ?? null;

      if (rootTracking == null && rootLeading == null) return;

      node.val = node.val.map(item => ({
        ...item,
        tracking: item.tracking ?? rootTracking,
        leading: item.leading ?? rootLeading,
      }));
    });
  }

  function saveScene() {
    const newScene = { ...state.actualScene };
    newScene.nodes = _.map(state.actualScene.nodes, (node) => {
      delete node.edited;
      delete node.renamed;
      return node;
    });
    newScene.edited = false;
    newScene.renamed = false;
    updateScene({ newScene, reSave: true });
  }
  function undoAllNodeChanges() {
    state.actualScene.tags = state.originalScene.tags;
    state.actualScene.description = state.originalScene.description;

    if (state.useCache && !state.currentBrandId) {
      sceneCacheStorage.clearSceneCache(state.actualScene.id)
    }

    state.actualScene.nodes.forEach(node => {
      undoNodeChanges({ node, removeTags: true });
    });
  }

  function checkConflicts(json) {
    let conflicts = false;
    const arraysEqual = (a = [], b = []) => {
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;
      const sortedA = [...a].map(String).sort();
      const sortedB = [...b].map(String).sort();
      return sortedA.every((val, i) => val === sortedB[i]);
    };
    const stringsEqual = (a = '', b = '') => String(a ?? '').trim() === String(b ?? '').trim();
    const hasField = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
    const isEmpty = (val) => {
      if (val == null) return true;
      if (Array.isArray(val) && val.length === 0) return true;
      if (typeof val === 'string' && val.trim() === '') return true;
      return false;
    };
    const checkFieldConflict = (current, jsonObj, key, comparator) => {
      if (isEmpty(current)) return false;
      if (!hasField(jsonObj, key)) return false;
      return !comparator(current, jsonObj[key]);
    };
    if (checkFieldConflict(state.actualScene.protected_regions, json, 'protected_regions', arraysEqual)) conflicts = true;
    if (checkFieldConflict(state.actualScene.tags, json, 'tags', arraysEqual)) conflicts = true;
    if (checkFieldConflict(state.actualScene.description, json, 'description', stringsEqual)) conflicts = true;
    const placeholders = [...(json.media || []), ...(json.text || []), ...(json.audio || [])];
    placeholders.forEach((ph) => {
      const node = state.nodesByKey.actual[ph.key];
      if (!node) return;
      if (checkFieldConflict(node.tags, ph, 'tags', arraysEqual)) conflicts = true;
      if (checkFieldConflict(node.description, ph, 'description', stringsEqual)) conflicts = true;
    });
    return conflicts;
  }

  function replaceData(json) {
    const hasField = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
    try {
      if (hasField(json, 'tags')) updateSceneTags(json.tags || []);
      if (hasField(json, 'description')) updateSceneDescription(json.description || '');
      if (hasField(json, 'protected_regions')) updateSceneProtectedRegions(json.protected_regions || []);
      const placeholders = [...(json.media || []), ...(json.text || []), ...(json.audio || [])];
      placeholders.forEach((ph) => {
        const node = state.nodesByKey.actual[ph.key];
        if (!node) return;
        if (hasField(ph, 'tags')) updateNodeTags({node, tags: ph.tags || []});
        if (hasField(ph, 'description')) updateNodeDescription({node, description: ph.description || ''});
      });
      return true;
    } catch (err) {
      console.error('replaceData error:', err);
      return false;
    }
  }

  function mergeData(json) {
    try {
      const mergedTags = Array.from(new Set([...state.actualScene.tags, ...(json.tags || [])]));
      if (mergedTags.length)
        updateSceneTags(mergedTags);
      if ((!state.actualScene.description || state.actualScene.description == '') && json.description && json.description.trim() !== '')
        updateSceneDescription(json.description);
      if ((!state.actualScene?.protected_regions?.length) && Array.isArray(json.protected_regions) && json.protected_regions.length)
        updateSceneProtectedRegions(json.protected_regions);
      const placeholders = [...(json.media || []), ...(json.text || []), ...(json.audio || [])];
      placeholders.forEach((ph) => {
        const node = state.nodesByKey.actual[ph.key];
        if (!node) return;
        const mergedNodeTags = Array.from(new Set([...(node.tags || []), ...(ph.tags || [])]));
        if (mergedNodeTags.length)
          updateNodeTags({node, tags: mergedNodeTags});
        if ((!node.description || node.description == '') && ph.description && ph.description.trim() !== '')
          updateNodeDescription({node, description: ph.description});
      });
      return true;
    } catch (err) {
      console.error('mergeData error:', err);
      return false;
    }
  }

  async function importSceneData(json) {
    const hasConflicts = checkConflicts(json);
    if (!hasConflicts) {
      replaceData(json);
      return true;
    }
    return false;
  }

  return {
    idmScene,
    scene,
    libraries,
    brands,
    updatedNodes,
    currentBrandId,
    hoveredNode,
    hoveredOnCanvas,
    selectedNode,
    debugMode,
    previewLoading,
    setIdmScene,
    updateScene,
    updateSceneDescription,
    updateSceneTags,
    setLibraries,
    setBrands,
    setCurrentBrandId,
    applyBrandStyles,
    hoverNode,
    selectNode,
    updateNodeValue,
    updateNodeTags,
    updateNodeDescription,
    showNode,
    hideNode,
    undoNodeChanges,
    setDebugMode,
    changePreviewLoading,
    standardization,
    saveScene,
    undoAllNodeChanges,
    replaceData,
    mergeData,
    importSceneData,
  };
});
